import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/libs/api";
import { useLocation, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Spin,
  Alert,
  Table,
  Tag,
  Progress,
  Space,
  theme,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  LeftOutlined,
  RightOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ScatterChart,
  Scatter,
  ZAxis,
  Cell,
  ReferenceLine,
} from "recharts";

import PageLayout from "@components/pageLayout/PageLayout.react";

const { Title, Text } = Typography;

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: Number(val).toFixed(2), suffix: unit };
}

// Australian states for carousel
const STATES = ["NSW", "SA", "QLD", "VIC"];
const STATE_NAMES = { NSW: "New South Wales", SA: "South Australia", QLD: "Queensland", VIC: "Victoria" };

// ============================================================================
// TIME UTILITY FUNCTIONS
// ============================================================================

/**
 * Get today's time range in AEST timezone (0:00 - 23:59 AEST)
 * Used for RRP Market chart (NEM time)
 * AEST = UTC+10 (no daylight saving adjustment for simplicity)
 * Returns UTC timestamps that correspond to AEST day boundaries
 */
function getAESTDayRange() {
  const AEST_OFFSET_MS = 10 * 60 * 60 * 1000; // UTC+10 in milliseconds
  const now = new Date();

  // Get current AEST date by adding 10 hours to UTC timestamp
  // This creates a Date where UTC methods return AEST values
  const aestNow = new Date(now.getTime() + AEST_OFFSET_MS);

  // Get AEST year/month/day using UTC methods
  const aestYear = aestNow.getUTCFullYear();
  const aestMonth = aestNow.getUTCMonth();
  const aestDay = aestNow.getUTCDate();

  // Start of AEST day (00:00:00 AEST) in UTC timestamp
  // AEST 00:00 = UTC previous day 14:00
  const startOfAESTDayUTC = Date.UTC(aestYear, aestMonth, aestDay, 0, 0, 0, 0) - AEST_OFFSET_MS;

  // End of AEST day (23:59:59.999 AEST) in UTC timestamp
  // AEST 23:59 = UTC same day 13:59
  const endOfAESTDayUTC = Date.UTC(aestYear, aestMonth, aestDay, 23, 59, 59, 999) - AEST_OFFSET_MS;

  return [startOfAESTDayUTC, endOfAESTDayUTC];
}

/**
 * Generate hourly tick values for X axis
 * @param {number} startTs - Start timestamp
 * @param {number} endTs - End timestamp
 * @param {number} intervalHours - Interval between ticks (default 3 hours)
 */
function generateHourlyTicks(startTs, endTs, intervalHours = 3) {
  const ticks = [];
  const intervalMs = intervalHours * 60 * 60 * 1000;
  for (let ts = startTs; ts <= endTs; ts += intervalMs) {
    ticks.push(ts);
  }
  return ticks;
}

// ============================================================================
// METRIC CARD COMPONENT
// ============================================================================
function MetricCard({ title, value, rise, icon, loading, color }) {
  const { token } = theme.useToken();
  const isPositive = rise >= 0;

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 14, marginBottom: 8, display: "block" }}>{title}</Text>
          <Statistic value={loading ? "—" : value} valueStyle={{ fontSize: 32, fontWeight: 700, color: token.colorText }} loading={loading} />
          {rise !== undefined && rise !== null && !loading && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 4 }}>
              {isPositive ? <ArrowUpOutlined style={{ color: token.colorSuccess, fontSize: 12 }} /> : <ArrowDownOutlined style={{ color: token.colorError, fontSize: 12 }} />}
              <Text style={{ color: isPositive ? token.colorSuccess : token.colorError, fontSize: 14, fontWeight: 500 }}>{Math.abs(rise).toFixed(1)}%</Text>
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>vs last period</Text>
            </div>
          )}
        </div>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: color || "rgba(84, 162, 204, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: token.colorPrimary }}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// VPP CAPACITY CARD WITH TIER MILESTONES
// ============================================================================
function CapacityCard({ data, loading: propLoading }) {
  const { token } = theme.useToken();

  // Use prop data or fallback to defaults
  const capacity = {
    current: data?.currentMW ?? 0,
    inverterKw: data?.inverterKW ?? 0,
    batteryKwh: data?.batteryKWh ?? 0,
  };
  const loading = propLoading;

  // Milestones: Tier 1 = 1MW, Tier 2 = 5MW, Tier 3 = 20MW
  const maxCapacity = 25; // Max for display purposes
  const progressPercent = Math.min(100, (capacity.current / maxCapacity) * 100);
  const tier1Pos = (1 / maxCapacity) * 100;
  const tier2Pos = (5 / maxCapacity) * 100;
  const tier3Pos = (20 / maxCapacity) * 100;

  const currentTier = capacity.current >= 20 ? 3 : capacity.current >= 5 ? 2 : capacity.current >= 1 ? 1 : 0;

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>VPP Capacity</Text>}
      styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: 16 } }}>
      {loading ? <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div> : (
        <div>
          {/* Current Capacity Display */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
            <div>
              <span style={{ fontSize: 36, fontWeight: 700, color: token.colorText }}>{capacity.current}</span>
              <span style={{ fontSize: 16, color: token.colorTextSecondary, marginLeft: 4 }}>MW</span>
            </div>
            <Tag color={currentTier >= 3 ? "gold" : currentTier >= 2 ? "blue" : currentTier >= 1 ? "green" : "default"} icon={<TrophyOutlined />}>
              {currentTier > 0 ? `Tier ${currentTier}` : "Pre-Tier 1"}
            </Tag>
          </div>

          {/* Progress Bar with Milestones */}
          <div style={{ position: "relative", marginBottom: 32 }}>
            <Progress
              percent={progressPercent}
              showInfo={false}
              strokeColor={{ "0%": token.colorPrimary, "100%": token.colorSuccess }}
              trailColor={token.colorBgLayout}
              style={{ marginBottom: 8 }}
            />

            {/* Milestone Markers */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 8 }}>
              {/* Tier 1 - 1MW */}
              <div style={{ position: "absolute", left: `${tier1Pos}%`, transform: "translateX(-50%)" }}>
                <div style={{ width: 2, height: 12, background: capacity.current >= 1 ? token.colorSuccess : token.colorBorder, marginTop: -2 }} />
                <div style={{ marginTop: 4, textAlign: "center", whiteSpace: "nowrap" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: capacity.current >= 1 ? token.colorSuccess : token.colorTextSecondary }}>1 MW</div>
                  <div style={{ fontSize: 9, color: token.colorTextSecondary }}>Tier 1</div>
                </div>
              </div>
              {/* Tier 2 - 5MW */}
              <div style={{ position: "absolute", left: `${tier2Pos}%`, transform: "translateX(-50%)" }}>
                <div style={{ width: 2, height: 12, background: capacity.current >= 5 ? token.colorPrimary : token.colorBorder, marginTop: -2 }} />
                <div style={{ marginTop: 4, textAlign: "center", whiteSpace: "nowrap" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: capacity.current >= 5 ? token.colorPrimary : token.colorTextSecondary }}>5 MW</div>
                  <div style={{ fontSize: 9, color: token.colorTextSecondary }}>Tier 2</div>
                </div>
              </div>
              {/* Tier 3 - 20MW */}
              <div style={{ position: "absolute", left: `${tier3Pos}%`, transform: "translateX(-50%)" }}>
                <div style={{ width: 2, height: 12, background: capacity.current >= 20 ? "#faad14" : token.colorBorder, marginTop: -2 }} />
                <div style={{ marginTop: 4, textAlign: "center", whiteSpace: "nowrap" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: capacity.current >= 20 ? "#faad14" : token.colorTextSecondary }}>20 MW</div>
                  <div style={{ fontSize: 9, color: token.colorTextSecondary }}>Tier 3</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ background: token.colorBgLayout, padding: 10, borderRadius: 8, textAlign: "center" }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Total Inverter</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{formatPower(capacity.inverterKw || 0, "kW").value} {formatPower(capacity.inverterKw || 0, "kW").suffix}</div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ background: token.colorBgLayout, padding: 10, borderRadius: 8, textAlign: "center" }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Total Battery</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{formatPower(capacity.batteryKwh || 0, "kWh").value} {formatPower(capacity.batteryKwh || 0, "kWh").suffix}</div>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// RECENT ACTIVITY CARD
// ============================================================================
function RecentActivityCard({ data, loading: propLoading }) {
  const { token } = theme.useToken();

  // Format API data - no mock fallback
  const activities = useMemo(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      return data.map((item, idx) => ({
        key: item.id || String(idx),
        time: item.time ? new Date(item.time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '--:--',
        event: item.type || 'Activity',
        system: item.system || 'Unknown',
        type: item.status === 'success' ? 'success' : item.status === 'warning' ? 'warning' : 'info',
      }));
    }
    return [];
  }, [data]);

  const loading = propLoading;

  const columns = [
    { title: "Time", dataIndex: "time", key: "time", width: 60, render: (t) => <Text type="secondary" style={{ fontSize: 12 }}>{t}</Text> },
    { title: "Event", dataIndex: "event", key: "event", render: (text, record) => (
      <Space size={4}>
        {record.type === "success" && <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 12 }} />}
        {record.type === "warning" && <ExclamationCircleOutlined style={{ color: token.colorWarning, fontSize: 12 }} />}
        {record.type === "info" && <SyncOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />}
        <span style={{ fontSize: 13 }}>{text}</span>
      </Space>
    )},
    { title: "System", dataIndex: "system", key: "system", render: (sn) => <Text code style={{ fontSize: 11 }}>{sn}</Text> },
  ];

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>Recent Activity</Text>}
      extra={<ClockCircleOutlined style={{ color: token.colorTextSecondary, fontSize: 14 }} />}
      styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: "8px 16px" } }}>
      <Table columns={columns} dataSource={activities} loading={loading} pagination={false} size="small" showHeader={false} />
    </Card>
  );
}

// ============================================================================
// SYSTEM STATUS CARD
// ============================================================================
function SystemStatusCard({ data, loading: propLoading }) {
  const { token } = theme.useToken();

  // Use prop data or fallback to defaults
  const status = {
    online: data?.online ?? 0,
    offline: data?.offline ?? 0,
    warning: data?.warning ?? 0,
    total: (data?.online ?? 0) + (data?.offline ?? 0) + (data?.warning ?? 0),
  };
  const loading = propLoading;

  const onlinePercent = status.total > 0 ? Math.round((status.online / status.total) * 100) : 0;

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>System Status</Text>}
      styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: 16 } }}>
      {loading ? <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div> : (
        <div>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <Progress type="dashboard" percent={onlinePercent} strokeColor={token.colorSuccess} size={120}
              format={(p) => <div><div style={{ fontSize: 24, fontWeight: 700 }}>{p}%</div><div style={{ fontSize: 11, color: token.colorTextSecondary }}>Online</div></div>} />
          </div>
          <Row gutter={8}>
            <Col span={8}>
              <div style={{ textAlign: "center", background: "rgba(5, 166, 119, 0.08)", padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: token.colorSuccess }}>{status.online}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>Online</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: "center", background: "rgba(250, 82, 82, 0.08)", padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: token.colorError }}>{status.offline}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>Offline</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: "center", background: "rgba(250, 173, 20, 0.08)", padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: token.colorWarning }}>{status.warning}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>Warning</Text>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// SYSTEM SCATTER PLOT
// ============================================================================
function SystemScatterPlot({ data, loading: propLoading }) {
  const { token } = theme.useToken();

  // Use prop data - no mock fallback
  const systems = useMemo(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      return data.map((item, idx) => ({
        id: item.sn || `SYS${String(idx + 1).padStart(3, "0")}`,
        inverterKw: Number(item.inverterKW) || 0,
        soc: Number(item.soc) || 0,
        pv: Number(item.pv) || 0,
        load: Number(item.load) || 0,
        online: item.online ?? true,
      }));
    }
    return [];
  }, [data]);

  const loading = propLoading;

  const onlineCount = systems.filter(s => s.online).length;
  const avgSOC = systems.length > 0 ? Math.round(systems.reduce((sum, s) => sum + s.soc, 0) / systems.length) : 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
      <div style={{ background: "#fff", padding: 8, borderRadius: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", fontSize: 11 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.id}</div>
        <div>Inverter: <strong>{p.inverterKw} kW</strong></div>
        <div>SOC: <strong>{p.soc}%</strong></div>
        <Tag color={p.online ? "success" : "error"} style={{ marginTop: 4, fontSize: 10 }}>{p.online ? "Online" : "Offline"}</Tag>
      </div>
    );
  };

  return (
    <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}
      title={<Text strong style={{ fontSize: 16 }}>System Distribution</Text>}
      extra={<Space size={8}><Tag color="success" style={{ fontSize: 10 }}>{onlineCount} Online</Tag><Tag style={{ fontSize: 10 }}>Avg SOC: {avgSOC}%</Tag></Space>}
      styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: 16 } }}>
      {loading ? (
        <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
      ) : systems.length === 0 ? (
        <div style={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text type="secondary">No Data</Text>
        </div>
      ) : (
        <>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 10, bottom: 25, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis dataKey="inverterKw" type="number" name="Inverter" domain={[0, 16]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
                  label={{ value: "Inverter (kW)", position: "bottom", offset: 5, fontSize: 10 }} />
                <YAxis dataKey="soc" type="number" name="SOC" domain={[0, 100]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={30}
                  label={{ value: "SOC %", angle: -90, position: "insideLeft", fontSize: 10 }} />
                <ZAxis range={[30, 30]} />
                <RechartsTooltip content={<CustomTooltip />} />
                <ReferenceLine y={20} stroke={token.colorError} strokeDasharray="3 3" />
                <ReferenceLine y={80} stroke={token.colorWarning} strokeDasharray="3 3" />
                <Scatter name="Systems" data={systems}>
                  {systems.map((entry, index) => (
                    <Cell key={index} fill={entry.online ? (entry.soc < 20 ? token.colorError : entry.soc > 80 ? token.colorWarning : token.colorSuccess) : token.colorTextDisabled} opacity={entry.online ? 0.8 : 0.4} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
            <Space size={4}><div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorSuccess }} /><Text type="secondary" style={{ fontSize: 10 }}>Normal (20-80%)</Text></Space>
            <Space size={4}><div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorError }} /><Text type="secondary" style={{ fontSize: 10 }}>Low (&lt;20%)</Text></Space>
            <Space size={4}><div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorWarning }} /><Text type="secondary" style={{ fontSize: 10 }}>High (&gt;80%)</Text></Space>
            <Space size={4}><div style={{ width: 8, height: 8, borderRadius: "50%", background: token.colorTextDisabled }} /><Text type="secondary" style={{ fontSize: 10 }}>Offline</Text></Space>
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================================
// RRP MARKET CHART (FULL WIDTH)
// ============================================================================
function parseTS(dateStr, timeStr) {
  if (!dateStr || !timeStr) return NaN;
  let y, m, d;
  const m1 = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dateStr);
  if (m1) { d = parseInt(m1[1], 10); m = parseInt(m1[2], 10); y = parseInt(m1[3], 10); }
  else {
    const m2 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!m2) return NaN;
    y = parseInt(m2[1], 10); m = parseInt(m2[2], 10); d = parseInt(m2[3], 10);
  }
  const t = /^(\d{1,2}):(\d{2})$/.exec(timeStr);
  if (!t) return NaN;
  return new Date(y, m - 1, d, parseInt(t[1], 10), parseInt(t[2], 10)).getTime();
}

function extractRRPData(datasets) {
  const byTs = new Map();
  const rrpArr = Array.isArray(datasets.rrpList) ? datasets.rrpList : datasets.mainData || [];
  for (const item of rrpArr) {
    if (!item) continue;
    // Prefer ts field from backend, fallback to parsing date/time
    const ts = item.ts || parseTS(item.date, item.time);
    if (!isFinite(ts)) continue;
    const value = item.value != null ? Number(item.value) : null;
    if (value != null && !isNaN(value)) byTs.set(ts, { ts, rrp: value });
  }
  return Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
}


function RRPMarketChart({ height = 280 }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [dataByState, setDataByState] = useState({});
  const [currentStateIndex, setCurrentStateIndex] = useState(0);
  const currentState = STATES[currentStateIndex];
  const carouselTimerRef = React.useRef(null);

  // Get fixed day range for X axis (AEST timezone 0:00-23:59)
  const [dayStart, dayEnd] = useMemo(() => getAESTDayRange(), []);
  const xAxisTicks = useMemo(() => generateHourlyTicks(dayStart, dayEnd, 3), [dayStart, dayEnd]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = {};
      for (const state of STATES) {
        try {
          const res = await api.get(`/api/dashboard/market?state=${state}`);
          console.log(`[RRP] Raw response for ${state}:`, JSON.stringify(res?.data).substring(0, 200));
          const payload = res?.data || {};
          const datasets = payload?.data && typeof payload.data === "object" ? payload.data : payload;
          const extracted = extractRRPData(datasets || {});
          console.log(`[RRP] Fetched ${state}: region=${datasets?.region}, points=${extracted.length}, lastPrice=${extracted[extracted.length-1]?.rrp}`);
          results[state] = extracted;
        } catch (err) {
          console.log(`[RRP] Fetch failed for ${state}:`, err.message);
          results[state] = []; // No mock fallback - empty array
        }
      }
      setDataByState(results);
    } catch (err) {
      console.error("[RRP] Fetch all failed:", err);
      // No mock fallback - empty state
      setDataByState({});
    } finally { setLoading(false); }
  }, []);

  // Start/reset carousel timer
  const startCarouselTimer = useCallback(() => {
    if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
    carouselTimerRef.current = setInterval(() => {
      setCurrentStateIndex(prev => (prev + 1) % STATES.length);
    }, 10000);
  }, []);

  // Handle manual state change - immediately switch and reset timer
  const handleStateChange = useCallback((newIndex) => {
    console.log("[RRP] handleStateChange called:", newIndex, "->", STATES[newIndex]);
    setCurrentStateIndex(newIndex);
    startCarouselTimer(); // Reset the 10-second timer
  }, [startCarouselTimer]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const id = setInterval(fetchData, 60000); return () => clearInterval(id); }, [fetchData]);
  useEffect(() => {
    startCarouselTimer();
    return () => { if (carouselTimerRef.current) clearInterval(carouselTimerRef.current); };
  }, [startCarouselTimer]);

  // Filter data to only include points within AEST day range for current state
  const data = useMemo(() => {
    const stateData = dataByState[currentState] || [];
    const filtered = stateData.filter(p => p.ts >= dayStart && p.ts <= dayEnd);
    console.log("[RRP] Data recalculated for", currentState, "- points:", filtered.length, "from", stateData.length);
    return filtered;
  }, [dataByState, currentState, dayStart, dayEnd]);

  // Y-axis domain: auto-scale based on visible data with padding
  const yDomain = useMemo(() => {
    if (!data.length) return [0, 200];
    let min = Infinity, max = -Infinity;
    for (const p of data) {
      if (p.rrp != null) {
        if (p.rrp < min) min = p.rrp;
        if (p.rrp > max) max = p.rrp;
      }
    }
    if (!isFinite(min)) return [0, 200];
    const range = max - min;
    const pad = Math.max(range * 0.15, 5); // At least 5 padding for small ranges
    return [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
  }, [data]);

  // Format timestamp as AEST time (UTC+10)
  const formatXTick = (ts) => {
    try {
      const AEST_OFFSET_MS = 10 * 60 * 60 * 1000;
      const aestDate = new Date(ts + AEST_OFFSET_MS);
      return `${String(aestDate.getUTCHours()).padStart(2, "0")}:${String(aestDate.getUTCMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  const currentPrice = data.length > 0 ? data[data.length - 1].rrp : 0;
  const prevPrice = data.length > 1 ? data[data.length - 2].rrp : currentPrice;
  const priceChange = currentPrice - prevPrice;
  console.log("[RRP] Render:", currentState, "price:", currentPrice, "dataPoints:", data.length);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div onClick={() => handleStateChange((currentStateIndex - 1 + STATES.length) % STATES.length)} style={{ cursor: "pointer", padding: 8, borderRadius: 8, background: token.colorBgLayout }}>
            <LeftOutlined style={{ color: token.colorTextSecondary, fontSize: 14 }} />
          </div>
          <div style={{ textAlign: "center", minWidth: 160 }}>
            <Text strong style={{ fontSize: 16 }}>{STATE_NAMES[currentState]}</Text>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4 }}>
              {STATES.map((s, i) => (
                <div key={s} style={{ width: 8, height: 8, borderRadius: "50%", background: i === currentStateIndex ? token.colorPrimary : token.colorBorderSecondary, cursor: "pointer" }} onClick={() => handleStateChange(i)} />
              ))}
            </div>
          </div>
          <div onClick={() => handleStateChange((currentStateIndex + 1) % STATES.length)} style={{ cursor: "pointer", padding: 8, borderRadius: 8, background: token.colorBgLayout }}>
            <RightOutlined style={{ color: token.colorTextSecondary, fontSize: 14 }} />
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Current RRP</Text>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: token.colorText }}>${currentPrice.toFixed(2)}</span>
            <span style={{ fontSize: 12, color: token.colorTextSecondary }}>/MWh</span>
            <Tag color={priceChange >= 0 ? "red" : "green"} style={{ marginLeft: 8 }}>{priceChange >= 0 ? "+" : ""}{priceChange.toFixed(2)}</Tag>
          </div>
        </div>
      </div>
      <div style={{ position: "relative", height }}>
        {loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.8)", zIndex: 10 }}><Spin /></div>}
        {!loading && data.length === 0 ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: token.colorTextSecondary }}>
            <Text type="secondary">No Data</Text>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart key={currentState} data={data} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id={`rrpGradient-${currentState}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.3}/><stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
              <XAxis dataKey="ts" type="number" domain={[dayStart, dayEnd]} ticks={xAxisTicks} tickFormatter={formatXTick} tick={{ fontSize: 11, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} allowDataOverflow />
              <YAxis domain={yDomain} tick={{ fontSize: 11, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `$${v}`} />
              <RechartsTooltip formatter={(v) => [`$${v.toFixed(2)}/MWh`, "RRP"]} labelFormatter={(ts) => formatXTick(ts)} />
              <Area type="monotone" dataKey="rrp" stroke={token.colorPrimary} strokeWidth={2} fill={`url(#rrpGradient-${currentState})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <Text type="secondary" style={{ display: "block", textAlign: "center", marginTop: 8, fontSize: 11 }}>Regional Reference Price as reported by AEMO. All times in NEM time.</Text>
    </div>
  );
}

// ============================================================================
// PV + LOAD CHART
// ============================================================================
function PVLoadChart({ height = 220 }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  // Get fixed day range for X axis (AEST timezone 0:00-23:59)
  const [dayStart, dayEnd] = useMemo(() => getAESTDayRange(), []);
  const xAxisTicks = useMemo(() => generateHourlyTicks(dayStart, dayEnd, 3), [dayStart, dayEnd]);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/api/dashboard/charts');
      const apiData = res?.data?.data?.pvLoad;
      if (apiData && Array.isArray(apiData) && apiData.length > 0) {
        setData(apiData);
      } else {
        setData([]); // No mock fallback
      }
    } catch (err) {
      console.error("PV/Load chart fetch error:", err);
      setData([]); // No mock fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  const currentPV = data.length > 0 ? data[data.length - 1].pv : 0;
  const currentLoad = data.length > 0 ? data[data.length - 1].load : 0;
  // Format timestamp as AEST time (UTC+10)
  const formatXTick = (ts) => {
    try {
      const AEST_OFFSET_MS = 10 * 60 * 60 * 1000;
      const aestDate = new Date(ts + AEST_OFFSET_MS);
      return `${String(aestDate.getUTCHours()).padStart(2, "0")}:${String(aestDate.getUTCMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Space size={16}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: "#faad14" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>PV</Text>
            <Text strong style={{ fontSize: 14, color: "#faad14" }}>{currentPV.toFixed(1)} kW</Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: token.colorPrimary }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Load</Text>
            <Text strong style={{ fontSize: 14, color: token.colorPrimary }}>{currentLoad.toFixed(1)} kW</Text>
          </div>
        </Space>
        <Tag color={currentPV > currentLoad ? "success" : "default"}>Net: {(currentPV - currentLoad).toFixed(1)} kW</Tag>
      </div>
      <div style={{ position: "relative", height }}>
        {loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.8)", zIndex: 10 }}><Spin /></div>}
        {!loading && data.length === 0 ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Text type="secondary">No Data</Text>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="pvGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#faad14" stopOpacity={0.3}/><stop offset="95%" stopColor="#faad14" stopOpacity={0}/></linearGradient>
                <linearGradient id="loadGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.3}/><stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0}/></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
              <XAxis dataKey="ts" type="number" domain={[dayStart, dayEnd]} ticks={xAxisTicks} tickFormatter={formatXTick} tick={{ fontSize: 10, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} allowDataOverflow />
              <YAxis tick={{ fontSize: 10, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} width={40} />
              <RechartsTooltip formatter={(v, name) => [`${v.toFixed(1)} kW`, name === "pv" ? "PV" : "Load"]} labelFormatter={(ts) => formatXTick(ts)} />
              <Area type="monotone" dataKey="pv" stroke="#faad14" strokeWidth={2} fill="url(#pvGradient)" />
              <Area type="monotone" dataKey="load" stroke={token.colorPrimary} strokeWidth={2} fill="url(#loadGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// HEADROOM + FOOTROOM CHART
// ============================================================================
function HeadroomFootroomChart({ height = 220 }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  // Get fixed day range for X axis (AEST timezone 0:00-23:59)
  const [dayStart, dayEnd] = useMemo(() => getAESTDayRange(), []);
  const xAxisTicks = useMemo(() => generateHourlyTicks(dayStart, dayEnd, 3), [dayStart, dayEnd]);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/api/dashboard/charts');
      const apiData = res?.data?.data?.headroomFootroom;
      if (apiData && Array.isArray(apiData) && apiData.length > 0) {
        setData(apiData);
      } else {
        setData([]); // No mock fallback
      }
    } catch (err) {
      console.error("Headroom/Footroom chart fetch error:", err);
      setData([]); // No mock fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  const currentHeadroom = data.length > 0 ? data[data.length - 1].headroom : 0;
  const currentFootroom = data.length > 0 ? data[data.length - 1].footroom : 0;
  // Format timestamp as AEST time (UTC+10)
  const formatXTick = (ts) => {
    try {
      const AEST_OFFSET_MS = 10 * 60 * 60 * 1000;
      const aestDate = new Date(ts + AEST_OFFSET_MS);
      return `${String(aestDate.getUTCHours()).padStart(2, "0")}:${String(aestDate.getUTCMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Space size={16}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: token.colorSuccess }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Headroom</Text>
            <Text strong style={{ fontSize: 14, color: token.colorSuccess }}>{Number(currentHeadroom).toFixed(1)} kW</Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: token.colorError }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Footroom</Text>
            <Text strong style={{ fontSize: 14, color: token.colorError }}>{Number(currentFootroom).toFixed(1)} kW</Text>
          </div>
        </Space>
        <Text type="secondary" style={{ fontSize: 11 }}>Available capacity</Text>
      </div>
      <div style={{ position: "relative", height }}>
        {loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.8)", zIndex: 10 }}><Spin /></div>}
        {!loading && data.length === 0 ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Text type="secondary">No Data</Text>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id="headroomGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3}/><stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0}/></linearGradient>
                <linearGradient id="footroomGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={token.colorError} stopOpacity={0.3}/><stop offset="95%" stopColor={token.colorError} stopOpacity={0}/></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
              <XAxis dataKey="ts" type="number" domain={[dayStart, dayEnd]} ticks={xAxisTicks} tickFormatter={formatXTick} tick={{ fontSize: 10, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} allowDataOverflow />
              <YAxis tick={{ fontSize: 10, fill: token.colorTextSecondary }} axisLine={false} tickLine={false} width={40} />
              <RechartsTooltip formatter={(v, name) => [`${Number(v).toFixed(1)} kW`, name === "headroom" ? "Headroom (discharge)" : "Footroom (charge)"]} labelFormatter={(ts) => formatXTick(ts)} />
              <Area type="monotone" dataKey="headroom" stroke={token.colorSuccess} strokeWidth={2} fill="url(#headroomGradient)" />
              <Area type="monotone" dataKey="footroom" stroke={token.colorError} strokeWidth={2} fill="url(#footroomGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
export default function Dashboard({ userId }) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  const params = useParams();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    
    api.get(`/api/dashboard/data`)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data?.data || res?.data || {};
        setDashboardData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Dashboard data fetch error:", err);
        setError(err?.message || "Failed to load dashboard data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userId, location?.search, params?.userId]);

  // Extract metrics from dashboard data
  const metricOnboarded = { value: dashboardData?.totalOnboarded ?? "—", rise: null };
  const metricOnline = { value: dashboardData?.totalOnline ?? "—", rise: null };
  const metricActivated = { value: dashboardData?.activatedSystem ?? "—", rise: null };

  const fullName = window._US?.name || localStorage.getItem("username") || "User";
  const firstName = fullName.split(" ")[0] || "User";

  return (
    <PageLayout active="dashboard">
      <div style={{ marginBottom: 20 }}>
        <Title level={2} style={{ marginBottom: 4 }}>Dashboard</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>Welcome back, {firstName}. Here's your VPP overview.</Text>
      </div>

      {error && <Alert message="Failed to load data" description={error} type="warning" showIcon style={{ marginBottom: 16 }} />}

      {/* Row 1: Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}><MetricCard title="Total Onboarded" value={metricOnboarded.value} rise={metricOnboarded.rise} icon={<TeamOutlined />} loading={loading} color="rgba(84, 162, 204, 0.1)" /></Col>
        <Col xs={24} sm={12} lg={8}><MetricCard title="Total Online" value={metricOnline.value} rise={metricOnline.rise} icon={<CheckCircleOutlined />} loading={loading} color="rgba(5, 166, 119, 0.1)" /></Col>
        <Col xs={24} sm={12} lg={8}><MetricCard title="Activated System" value={metricActivated.value} rise={metricActivated.rise} icon={<AppstoreOutlined />} loading={loading} color="rgba(245, 183, 89, 0.1)" /></Col>
      </Row>

      {/* Row 2: VPP Capacity (40%) + Recent Activity (60%) */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}><CapacityCard data={dashboardData?.vppCapacity} loading={loading} /></Col>
        <Col xs={24} lg={14}><RecentActivityCard data={dashboardData?.recentActivity} loading={loading} /></Col>
      </Row>

      {/* Row 3: System Status + System Distribution */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}><SystemStatusCard data={dashboardData?.systemStatus} loading={loading} /></Col>
        <Col xs={24} lg={14}><SystemScatterPlot data={dashboardData?.systemDistribution} loading={loading} /></Col>
      </Row>

      {/* Row 4: Live Market Data (Full Width) */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title={<Space><ThunderboltOutlined style={{ color: token.colorPrimary }} /><span style={{ fontSize: 16, fontWeight: 600 }}>Live Market Data</span></Space>}
            bordered={false} style={{ borderRadius: 12 }} styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: 16 } }}>
            <RRPMarketChart height={280} />
          </Card>
        </Col>
      </Row>

      {/* Row 5: PV & Load + Headroom & Footroom */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<Space><span style={{ color: "#faad14" }}>☀</span><span style={{ fontSize: 16, fontWeight: 600 }}>PV & Load</span></Space>}
            bordered={false} style={{ borderRadius: 12 }} styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: 16 } }}>
            <PVLoadChart height={200} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<Space><span>⚡</span><span style={{ fontSize: 16, fontWeight: 600 }}>Headroom & Footroom</span></Space>}
            bordered={false} style={{ borderRadius: 12 }} styles={{ header: { borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: "12px 16px", minHeight: 48 }, body: { padding: 16 } }}>
            <HeadroomFootroomChart height={200} />
          </Card>
        </Col>
      </Row>
    </PageLayout>
  );
}
